import * as THREE from 'three';
import { Text as TroikaText } from 'troika-three-text';
import { generateUniqueId } from '../utils/generateUniqueId';
import { ColorIndex1 } from '../utils/colorIndex';

// Bazı GPU/driver kombinasyonlarında WebGL1 ANGLE_instanced_arrays extension'ını
// sunmuyor; webgl-sdf-generator throw ediyor. Troika her seferinde GPU yolunu
// deneyip JS worker'a düşüyor (çalışıyor ama console hatası ve gecikme var).
// `gpuAccelerateSDF=false` ile direkt JS path'e zorluyoruz.
function Text() {
  const m = new TroikaText();
  m.gpuAccelerateSDF = false;
  return m;
}

/**
 * DXF font adını Troika font yoluna çevir
 */
function mapFontToPath(fontName, bold = false, italic = false) {
  // public/fonts'ta YALNIZCA Arial varyantları var (Arial.ttf, ARIALBD/ARIALI/ARIALBI).
  // Times/Courier/ArialNarrow .TTF dosyaları YOK — eski mapping bunları /fonts/TIMES.TTF
  // gibi olmayan yollara yönlendiriyordu → 404 → troika fontu yükleyemeyip kat planı
  // mtext yazıları GÖRÜNMÜYORDU ("Oluştur" sonrası textler yok). Tüm fontları mevcut
  // Arial varyantına düşür (eksik font dosyaları elde yok; görünürlük > font sadakati).
  // fontName şimdilik kullanılmıyor; ileride başka .TTF eklenirse burada eşlenebilir.
  if (bold && italic) return '/fonts/ARIALBI.TTF';
  if (bold) return '/fonts/ARIALBD.TTF';
  if (italic) return '/fonts/ARIALI.TTF';
  return '/fonts/Arial.ttf';
}

/**
 * MTEXT text içeriğini segment'lere ayırarak parse eder.
 * Her segment kendi formatting bilgisini taşır.
 */
export function parseMtextSegments(rawText, baseColor, baseHeight) {
  if (!rawText) return { segments: [], plainText: '', globalFormatting: {} };

  // Escape sequences ön işleme
  let text = rawText.replace(/\\\\/g, '\x00BS\x00'); // \\ → placeholder

  const segments = [];
  const globalFormatting = {
    align: null,
    leftIndent: 0,      // \pl - paragraf sol girintisi
    firstLineIndent: 0, // \pi - ilk satır girintisi
    tabStops: [],       // t56,t112 - tab durakları
  };

  // Aktif formatting state - her segment bunu kopyalar
  let currentFormat = {
    color: baseColor,
    height: baseHeight,
    heightMultiplier: 1,
    bold: false,
    italic: false,
    underline: false,
    overline: false,
    strikethrough: false,
    fontName: null,
  };

  // Format stack - süslü parantezler için
  const formatStack = [];

  let currentText = '';
  let pendingSpace = ''; // Format kodu öncesi boşlukları sonraki segment'e taşımak için

  function flushSegment() {
    if (currentText.length > 0) {
      // Sondaki boşlukları ayır - sonraki segment'in başına eklenecek
      const trimmedText = currentText.trimEnd();
      const trailingSpaces = currentText.slice(trimmedText.length);
      const startsWithSpace = /^\s/.test(trimmedText);
      const endsWithSpace = trailingSpaces.length > 0;

      if (trimmedText.length > 0) {
        segments.push({
          text: trimmedText.replace(/\x00BS\x00/g, '\\'),
          color: currentFormat.color,
          height: currentFormat.height * currentFormat.heightMultiplier,
          bold: currentFormat.bold,
          italic: currentFormat.italic,
          underline: currentFormat.underline,
          overline: currentFormat.overline,
          strikethrough: currentFormat.strikethrough,
          fontName: currentFormat.fontName,
          startsWithSpace, // Kelime grupları için
          endsWithSpace,   // Kelime grupları için
        });
      }

      // Boşlukları sonraki segment için sakla
      pendingSpace = trailingSpaces;
      currentText = '';
    }
  }

  // Pending space'i current text'e ekle (yeni segment başlarken çağrılır)
  function applyPendingSpace() {
    if (pendingSpace) {
      currentText = pendingSpace + currentText;
      pendingSpace = '';
    }
  }

  function parseColorValue(code, value) {
    if (code === 'C') {
      // ACI Color
      const aci = parseInt(value, 10);
      if (aci !== 256 && aci !== 0) {
        const c = ColorIndex1(aci).trim();
        if (c && c.length > 0) return c;
      }
    } else if (code === 'c') {
      // True Color (packed RGB)
      const packed = parseInt(value, 10);
      const r = (packed >> 16) & 0xFF;
      const g = (packed >> 8) & 0xFF;
      const b = packed & 0xFF;
      return `rgb(${r},${g},${b})`;
    }
    return null;
  }

  let i = 0;
  while (i < text.length) {
    // ^I = Tab - özel segment olarak işle
    if (text[i] === '^' && text[i + 1] === 'I') {
      flushSegment();
      // Tab için özel segment oluştur
      segments.push({
        isTab: true,
        color: currentFormat.color,
        height: currentFormat.height * currentFormat.heightMultiplier,
      });
      i += 2;
      continue;
    }

    // { = Yeni format grubu başlat
    if (text[i] === '{') {
      flushSegment();
      formatStack.push({ ...currentFormat });
      i++;
      continue;
    }

    // } = Format grubunu kapat, önceki state'e dön
    if (text[i] === '}') {
      flushSegment();
      if (formatStack.length > 0) {
        currentFormat = formatStack.pop();
      }
      i++;
      continue;
    }

    // Backslash format kodları
    if (text[i] === '\\' && i + 1 < text.length) {
      const code = text[i + 1];

      // \P = Yeni satır
      if (code === 'P') {
        applyPendingSpace();
        currentText += '\n';
        i += 2;
        continue;
      }

      // \~ = Non-breaking space
      if (code === '~') {
        applyPendingSpace();
        currentText += ' ';
        i += 2;
        continue;
      }

      // Escape karakterleri
      if (code === '{' || code === '}') {
        applyPendingSpace();
        currentText += code;
        i += 2;
        continue;
      }

      // Toggle kodları - format değişikliği, segment'i flush et
      if (code === 'L') {
        flushSegment();
        currentFormat.underline = true;
        i += 2;
        continue;
      }
      if (code === 'l') {
        flushSegment();
        currentFormat.underline = false;
        i += 2;
        continue;
      }
      if (code === 'O') {
        flushSegment();
        currentFormat.overline = true;
        i += 2;
        continue;
      }
      if (code === 'o') {
        flushSegment();
        currentFormat.overline = false;
        i += 2;
        continue;
      }
      if (code === 'K') {
        flushSegment();
        currentFormat.strikethrough = true;
        i += 2;
        continue;
      }
      if (code === 'k') {
        flushSegment();
        currentFormat.strikethrough = false;
        i += 2;
        continue;
      }

      // Semicolon ile biten kodlar
      const sc = text.indexOf(';', i + 1);
      if (sc > i) {
        const formatStr = text.slice(i + 2, sc);

        // \C = ACI Color
        if (code === 'C') {
          flushSegment();
          const newColor = parseColorValue('C', formatStr);
          if (newColor) currentFormat.color = newColor;
          i = sc + 1;
          continue;
        }

        // \c = True Color
        if (code === 'c') {
          flushSegment();
          const newColor = parseColorValue('c', formatStr);
          if (newColor) currentFormat.color = newColor;
          i = sc + 1;
          continue;
        }

        // \H = Height
        if (code === 'H') {
          flushSegment();
          if (formatStr.endsWith('x')) {
            // Multiplier - mevcut efektif yüksekliğe uygula
            const multiplier = parseFloat(formatStr.slice(0, -1)) || 1;
            const currentEffectiveHeight = currentFormat.height * currentFormat.heightMultiplier;
            currentFormat.height = currentEffectiveHeight * multiplier;
            currentFormat.heightMultiplier = 1;
          } else {
            // Mutlak yükseklik
            currentFormat.height = parseFloat(formatStr) || baseHeight;
            currentFormat.heightMultiplier = 1;
          }
          i = sc + 1;
          continue;
        }

        // \f veya \F = Font
        if (code === 'f' || code === 'F') {
          flushSegment();
          const parts = formatStr.split('|');
          currentFormat.fontName = parts[0];
          parts.slice(1).forEach(p => {
            if (p.startsWith('b')) currentFormat.bold = p[1] === '1';
            else if (p.startsWith('i')) currentFormat.italic = p[1] === '1';
          });
          i = sc + 1;
          continue;
        }

        // \A = Alignment (global)
        if (code === 'A') {
          const v = parseInt(formatStr, 10);
          globalFormatting.align = v === 0 ? 'left' : v === 1 ? 'center' : v === 2 ? 'right' : 'left';
          i = sc + 1;
          continue;
        }

        // \S = Stacked text (süperscript/subscript)
        if (code === 'S') {
          flushSegment();
          // Ayırıcılar: ^ (tolerance), / (fraction), # (horizontal)
          const separators = ['^', '/', '#'];
          for (const sep of separators) {
            const idx = formatStr.indexOf(sep);
            if (idx > -1) {
              const topPart = formatStr.slice(0, idx);
              const bottomPart = formatStr.slice(idx + 1);
              const stackHeight = currentFormat.height * currentFormat.heightMultiplier;

              // Stacked text içindeki boşlukları kontrol et
              // superText veya subText başında boşluk varsa kelime başlangıcı
              // superText veya subText sonunda boşluk varsa kelime sonu
              const superStartsWithSpace = /^\s/.test(topPart);
              const subStartsWithSpace = /^\s/.test(bottomPart);
              const superEndsWithSpace = /\s$/.test(topPart);
              const subEndsWithSpace = /\s$/.test(bottomPart);

              // Kelime grupları için: başta veya sonda boşluk var mı?
              const startsWithSpace = pendingSpace.length > 0 || superStartsWithSpace || subStartsWithSpace;
              const endsWithSpace = superEndsWithSpace || subEndsWithSpace;

              // Tek bir stacked segment oluştur, rendering'de iki mesh yapılacak
              segments.push({
                isStackedGroup: true,
                superText: topPart.trim(), // Boşlukları temizle
                subText: bottomPart.trim(), // Boşlukları temizle
                color: currentFormat.color,
                height: stackHeight,
                fontName: currentFormat.fontName,
                bold: currentFormat.bold,
                italic: currentFormat.italic,
                leadingSpace: pendingSpace, // Önceki segment'ten gelen boşluk
                startsWithSpace, // Kelime grupları için
                endsWithSpace, // Kelime grupları için
              });
              pendingSpace = ''; // Kullanıldı, temizle
              break;
            }
          }
          i = sc + 1;
          continue;
        }

        // \p = Paragraph formatting (pl, pi, pt, pr, pq, px)
        if (code === 'p') {
          // formatStr örnek: "l14,t56" veya "l14,t56,t112"
          const parts = formatStr.split(',');
          for (const part of parts) {
            const trimmed = part.trim();
            if (trimmed.startsWith('l')) {
              // pl = left indent
              globalFormatting.leftIndent = parseFloat(trimmed.slice(1)) || 0;
            } else if (trimmed.startsWith('i')) {
              // pi = first line indent
              globalFormatting.firstLineIndent = parseFloat(trimmed.slice(1)) || 0;
            } else if (trimmed.startsWith('t')) {
              // t = tab stop
              const tabPos = parseFloat(trimmed.slice(1));
              if (!isNaN(tabPos)) {
                globalFormatting.tabStops.push(tabPos);
              }
            }
          }
          // Tab stop'ları sırala
          globalFormatting.tabStops.sort((a, b) => a - b);
          i = sc + 1;
          continue;
        }

        // Diğer kodları atla (W, Q, T, vs.)
        i = sc + 1;
        continue;
      }

      // Semicolon yok - literal backslash
      currentText += '\\';
      i++;
      continue;
    }

    // Normal karakter
    let end = i;
    while (end < text.length &&
           text[end] !== '\\' &&
           text[end] !== '{' &&
           text[end] !== '}' &&
           !(text[end] === '^' && text[end + 1] === 'I')) {
      end++;
    }

    if (end > i) {
      // Pending space'i önce ekle (format kodundan sonraki ilk karakter)
      applyPendingSpace();
      currentText += text.slice(i, end);
    }
    i = end;
  }

  // Kalan text'i flush et
  flushSegment();

  // Plain text hesapla
  const plainText = segments.map(s => {
    if (s.isTab) return '\t';
    if (s.isStackedGroup) return (s.superText || '') + (s.subText || '');
    return s.text || '';
  }).join('');

 

  return { segments, plainText, globalFormatting };
}

/**
 * Eski uyumluluk için - sadece plain text ve genel formatting döndürür
 */
export function parseMtextContent(rawText) {
  const { plainText, globalFormatting } = parseMtextSegments(rawText, '#ffffff', 10);
  return { plainText, formatting: globalFormatting };
}

/**
 * Draws an MTEXT entity with inline formatting support.
 * Her farklı formatlı segment için ayrı Text mesh oluşturur.
 */
export function drawMtextFromCode(target, entity) {
  try {
    const rawText = entity.TextString ?? entity.text ?? '';

    // Base color hesapla
    let baseColor = '#ffffff';
    if (entity.colorIndex != null && entity.colorIndex !== 256 && entity.colorIndex !== 0) {
      const c = ColorIndex1(entity.colorIndex).trim();
      if (c && c.length > 0) baseColor = c;
    } else if (entity.color != null && entity.color !== 256 && entity.color !== 0) {
      const c = typeof entity.color === 'string' ? entity.color.trim() : ColorIndex1(entity.color).trim();
      if (c && c.length > 0) baseColor = c;
    } else if (entity.layerColor != null) {
      if (typeof entity.layerColor === 'number') {
        const c = ColorIndex1(entity.layerColor).trim();
        if (c && c.length > 0) baseColor = c;
      } else if (typeof entity.layerColor === 'string') {
        baseColor = entity.layerColor.trim() || baseColor;
      } else {
        baseColor = '#' + entity.layerColor.toString(16).padStart(6, '0');
      }
    }

    const baseHeight = entity.height || 10;
    const { segments, plainText, globalFormatting } = parseMtextSegments(rawText, baseColor, baseHeight);

 
    // Text alignment
    let textAlign = globalFormatting.align || 'left';
    const hAlignMap = {
      1: 'left', 2: 'center', 3: 'right',
      4: 'left', 5: 'center', 6: 'right',
      7: 'left', 8: 'center', 9: 'right',
    };
    if (!globalFormatting.align && entity.attachmentPoint) {
      textAlign = hAlignMap[entity.attachmentPoint] || 'left';
    }

    // DXF height = cap height, Troika fontSize = em height
    // AutoCAD'e daha yakın görünüm için 0.70 kullanılıyor
    const CAP_HEIGHT_RATIO = 0.70;

    // Rotation hesapla
    let rotationRad = 0;
    if (entity.directionVector && (entity.directionVector.x !== undefined || entity.directionVector.y !== undefined)) {
      rotationRad = Math.atan2(entity.directionVector.y || 0, entity.directionVector.x || 1);
    } else if (entity.rotation) {
      rotationRad = THREE.MathUtils.degToRad(entity.rotation);
    }

    // Anchor hesapla
    const anchor = {
      1: { x: 'left', y: 'top-cap' }, 2: { x: 'center', y: 'top-cap' }, 3: { x: 'right', y: 'top-cap' },
      4: { x: 'left', y: 'middle' }, 5: { x: 'center', y: 'middle' }, 6: { x: 'right', y: 'middle' },
      7: { x: 'left', y: 'bottom' }, 8: { x: 'center', y: 'bottom' }, 9: { x: 'right', y: 'bottom' },
    }[entity.attachmentPoint] || { x: 'left', y: 'top-cap' };

    // Eğer tek segment varsa veya tüm segmentler aynı formattaysa, tek mesh kullan
    const allSameColor = segments.length <= 1 || segments.every(s => s.color === segments[0].color);
    const allSameHeight = segments.length <= 1 || segments.every(s => Math.abs(s.height - segments[0].height) < 0.01);
    const allSameFont = segments.length <= 1 || segments.every(s => s.fontName === segments[0].fontName);
    const hasStackedText = segments.some(s => s.isStackedGroup);
    const hasTabs = segments.some(s => s.isTab);
    const hasParagraphFormatting = globalFormatting.leftIndent > 0 ||
                                    globalFormatting.firstLineIndent !== 0 ||
                                    globalFormatting.tabStops.length > 0;

    // Basit durumlar için tek mesh kullan (performans için)
    // Font değişikliği, tab, stacked text veya paragraf formatı varsa segment-based kullan
    if (allSameColor && allSameHeight && allSameFont && !hasStackedText && !hasTabs && !hasParagraphFormatting) {
      return createSingleMesh(entity, plainText, baseColor, baseHeight, textAlign, anchor, rotationRad, rawText, globalFormatting, segments);
    }

 

    // Karmaşık durumlar (farklı renkler/boyutlar) için segment-based rendering
    const group = new THREE.Group();
    group.position.set(entity.position?.x || 0, entity.position?.y || 0, 0);
    group.rotation.z = rotationRad;

    const defaultFontPath = entity.fontPath || '/fonts/Arial.ttf';
    const maxWidth = entity.width && entity.width > 0 ? entity.width : Infinity;

    // Paragraf girintileri
    const leftIndent = globalFormatting.leftIndent || 0;
    const firstLineIndent = globalFormatting.firstLineIndent || 0;
    const tabStops = globalFormatting.tabStops || [];

 
    // Satırlara ayır (\n ve word-wrap)
    const lines = wrapSegmentsToLines(segments, maxWidth, CAP_HEIGHT_RATIO, leftIndent, firstLineIndent, tabStops, baseHeight);

    let yOffset = 0;
    // AutoCAD'de satır yüksekliği genellikle text height * 1.5-1.6 civarında
    const lineHeight = (baseHeight / CAP_HEIGHT_RATIO) * 1.4;
    let isFirstLine = true;

    for (const lineSegments of lines) {
      // Satır başlangıç pozisyonu
      // Tab ile başlayan satırlar için 0'dan başla (tab mutlak pozisyon verecek)
      // Normal satırlar için leftIndent uygula
      const lineStartsWithTab = lineSegments.length > 0 && lineSegments[0].isTab;
      let xOffset = lineStartsWithTab ? 0 : (leftIndent + (isFirstLine ? firstLineIndent : 0));
      isFirstLine = false;

      for (const seg of lineSegments) {
        // Tab segment - mutlak pozisyona atla (t56 = 56 birim)
        if (seg.isTab) {
          const prevXOffset = xOffset;
          const nextTabStop = tabStops.find(t => t > xOffset);
          if (nextTabStop !== undefined) {
            xOffset = nextTabStop; // Mutlak pozisyon, ekleme yok
          } else {
            // Tab stop yoksa varsayılan tab genişliği
            const defaultTabWidth = (baseHeight / CAP_HEIGHT_RATIO) * 4;
            xOffset = Math.ceil(xOffset / defaultTabWidth) * defaultTabWidth + defaultTabWidth;
          }
           continue;
        }

        // Stacked group (superscript/subscript) - iki mesh oluştur
        if (seg.isStackedGroup) {
          // Leading space varsa xOffset'e ekle
          if (seg.leadingSpace) {
            const spaceWidth = estimateTextWidthRaw(seg.leadingSpace, baseHeight / CAP_HEIGHT_RATIO, seg.fontName);
            xOffset += spaceWidth;
          }

          const stackFontSize = (seg.height * 0.6) / CAP_HEIGHT_RATIO;
          const stackFont = seg.fontName ? mapFontToPath(seg.fontName, seg.bold, seg.italic) : defaultFontPath;
          let maxStackWidth = 0;

          // Superscript (üstte - baseline hizasında)
          if (seg.superText) {
            const superMesh = new Text();
            superMesh.fontSize = stackFontSize;
            superMesh.font = stackFont;
            superMesh.color = seg.color;
            superMesh.text = seg.superText;
            superMesh.anchorX = 'left';
            superMesh.anchorY = 'bottom';
            // Ana metnin ortasına hizala (top-cap'ten aşağı)
            superMesh.position.set(xOffset, yOffset - seg.height * 0.35, 0);
            superMesh.userData = { segmentText: seg.superText, isSuper: true };
            group.add(superMesh);

            const superWidth = estimateTextWidthRaw(seg.superText, stackFontSize, seg.fontName);
            maxStackWidth = Math.max(maxStackWidth, superWidth);
          }

          // Subscript (altta - baseline'ın altında)
          if (seg.subText) {
            const subMesh = new Text();
            subMesh.fontSize = stackFontSize;
            subMesh.font = stackFont;
            subMesh.color = seg.color;
            subMesh.text = seg.subText;
            subMesh.anchorX = 'left';
            subMesh.anchorY = 'top';
            // Superscript'in hemen altında
            subMesh.position.set(xOffset, yOffset - seg.height * 0.55, 0);
            subMesh.userData = { segmentText: seg.subText, isSub: true };
            group.add(subMesh);

            const subWidth = estimateTextWidthRaw(seg.subText, stackFontSize, seg.fontName);
            maxStackWidth = Math.max(maxStackWidth, subWidth);
          }

          xOffset += maxStackWidth;
          continue;
        }

        if (!seg.text || seg.text.length === 0) continue;

        const mesh = new Text();
        const troikaFontSize = seg.height / CAP_HEIGHT_RATIO;
        // Segment'in kendi font'unu kullan, yoksa varsayılan
        const segmentFont = seg.fontName ? mapFontToPath(seg.fontName, seg.bold, seg.italic) : defaultFontPath;

        mesh.fontSize = troikaFontSize;
        mesh.font = segmentFont;
        mesh.color = seg.color;
        mesh.text = seg.text;
        mesh.anchorX = 'left';
        mesh.anchorY = 'top-cap';
        mesh.position.set(xOffset, yOffset, 0);

        // Tahmini genişlik (font'a göre ayarlanabilir)
        const estimatedWidth = estimateTextWidth(seg.text, troikaFontSize, seg.fontName, seg.bold, seg.italic);
        xOffset += estimatedWidth;

        mesh.userData = {
          segmentText: seg.text,
          segmentColor: seg.color,
          segmentHeight: seg.height,
          segmentFont: segmentFont,
        };

        group.add(mesh);
       }

      yOffset -= lineHeight;
    }

 
    // Group userData
    group.userData = {
      id: generateUniqueId('mtext'),
      dxfHandle: entity.handle,
      type: 'mtext',
      position: entity.position,
      rawText: rawText,
      plainText: plainText,
      baseFontSize: baseHeight,
      baseColorIndex: entity.colorIndex,
      baseColor: entity.color,
      layerColor: entity.layerColor,
      renderedColor: baseColor,
      width: entity.width,
      attachmentPoint: entity.attachmentPoint,
      rotation: entity.rotation || (rotationRad * 180 / Math.PI),
      directionVector: entity.directionVector,
      textAlign: textAlign,
      anchorY: anchor.y,
      originalColor: baseColor,
      layerName: entity.layerName || '0',
      segments: segments,
      isSegmented: true,
    };

    if (target?.add) {
      target.add(group);
    }

    return group;

  } catch (error) {
    console.error('MTEXT ERROR:', error);
    return null;
  }
}

/**
 * Text genişliğini tahmin et (güvenlik marjı olmadan)
 */
function estimateTextWidthRaw(text, fontSize, fontName, bold = false, italic = false) {
  if (!text) return 0;

  // Font tipine göre genişlik çarpanı
  const isMonospace = fontName && /courier|mono|consolas|fixedsys/i.test(fontName);
  const isCondensed = fontName && /narrow|condensed|compressed/i.test(fontName);
  const isWide = fontName && /wide|extended|expanded/i.test(fontName);
  const isTimes = fontName && /times|roman|serif/i.test(fontName);

  // Monospace fontlar için sabit genişlik
  if (isMonospace) {
    let w = text.length * fontSize * 0.6;
    if (bold) w *= 1.1;
    return w;
  }

  // Karakter genişlik faktörleri
  let width = 0;
  for (const char of text) {
    let charWidth;
    if (char === ' ' || char === '\t') {
      charWidth = fontSize * 0.3;
    } else if (char.match(/[ilIj1|!.,;:'"]/)) {
      charWidth = fontSize * 0.3; // Dar karakterler
    } else if (char.match(/[mwMW@%]/)) {
      charWidth = fontSize * 0.85; // Geniş karakterler
    } else if (char.match(/[A-Z]/)) {
      charWidth = fontSize * 0.7; // Büyük harfler
    } else if (char.match(/[a-z]/)) {
      charWidth = fontSize * 0.5; // Küçük harfler
    } else if (char.match(/[0-9]/)) {
      charWidth = fontSize * 0.55; // Rakamlar
    } else {
      charWidth = fontSize * 0.55; // Diğer karakterler
    }

    // Times/Serif fontlar biraz daha dar
    if (isTimes) {
      charWidth *= 0.9;
    }
    // Condensed fontlar daha dar
    if (isCondensed) {
      charWidth *= 0.75;
    }
    // Wide fontlar daha geniş
    if (isWide) {
      charWidth *= 1.25;
    }

    width += charWidth;
  }

  // Bold fontlar daha geniş
  if (bold) {
    width *= 1.1;
  }
  // Italic fontlar biraz daha geniş (eğim nedeniyle)
  if (italic) {
    width *= 1.05;
  }

  return width;
}

/**
 * Text genişliğini tahmin et (font tipine göre, güvenlik marjı ile)
 * @param {string} text - Metin
 * @param {number} fontSize - Font boyutu
 * @param {string} fontName - DXF font adı (opsiyonel)
 * @param {boolean} bold - Kalın mı
 * @param {boolean} italic - İtalik mi
 */
function estimateTextWidth(text, fontSize, fontName, bold = false, italic = false) {
  // Güvenlik marjı kaldırıldı - fazla boşluk ve erken wrap sorununa neden oluyordu
  return estimateTextWidthRaw(text, fontSize, fontName, bold, italic);
}

/**
 * Segment genişliğini hesapla
 */
function calculateSegmentWidth(seg, capHeightRatio) {
  if (seg.isTab) return 0;

  if (seg.isStackedGroup) {
    let width = 0;
    if (seg.leadingSpace) {
      width += estimateTextWidthRaw(seg.leadingSpace, seg.height / capHeightRatio, seg.fontName, seg.bold, seg.italic);
    }
    const stackFontSize = (seg.height * 0.6) / capHeightRatio;
    const superWidth = seg.superText ? estimateTextWidthRaw(seg.superText, stackFontSize, seg.fontName, seg.bold, seg.italic) : 0;
    const subWidth = seg.subText ? estimateTextWidthRaw(seg.subText, stackFontSize, seg.fontName, seg.bold, seg.italic) : 0;
    width += Math.max(superWidth, subWidth);
    return width;
  }

  if (!seg.text) return 0;
  const troikaFontSize = seg.height / capHeightRatio;
  return estimateTextWidth(seg.text, troikaFontSize, seg.fontName, seg.bold, seg.italic);
}

/**
 * Segments'i satırlara ayır (\n ve word-wrap ile)
 * Aralarında boşluk olmayan segment'ler tek kelime gibi davranır
 */
function wrapSegmentsToLines(segments, maxWidth, capHeightRatio, leftIndent = 0, firstLineIndent = 0, tabStops = [], baseHeight = 10) {
  const lines = [[]];
  let isFirstLine = true;
  // İlk satır tab ile başlıyorsa 0'dan başla (rendering'deki mantık ile uyumlu)
  let currentLineWidth = 0;

  // Önce segment'leri kelime gruplarına ayır
  // Kelime grubu: Aralarında boşluk olmayan ardışık segment'ler
  const wordGroups = [];
  let currentGroup = [];

  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];

    // Tab her zaman ayrı grup
    if (seg.isTab) {
      if (currentGroup.length > 0) {
        wordGroups.push({ segments: currentGroup, isWordGroup: true });
        currentGroup = [];
      }
      wordGroups.push({ segments: [seg], isTab: true });
      continue;
    }

    // Segment'i gruba ekle
    currentGroup.push(seg);

    // Bu segment kelime sonu mu? (sonunda boşluk var veya son segment)
    const isLastSegment = i === segments.length - 1;
    const hasTrailingSpace = seg.endsWithSpace || (seg.text && /\s$/.test(seg.text));

    // Sonraki segment başında boşluk var mı?
    const nextSeg = segments[i + 1];
    const nextStartsWithSpace = nextSeg && (nextSeg.startsWithSpace || nextSeg.leadingSpace || (nextSeg.text && /^\s/.test(nextSeg.text)));

    if (isLastSegment || hasTrailingSpace || nextStartsWithSpace || (nextSeg && nextSeg.isTab)) {
      if (currentGroup.length > 0) {
        wordGroups.push({ segments: currentGroup, isWordGroup: true });
        currentGroup = [];
      }
    }
  }

  if (currentGroup.length > 0) {
    wordGroups.push({ segments: currentGroup, isWordGroup: true });
  }

  // Kelime gruplarını işle
  for (const group of wordGroups) {
    // Tab segment - currentLineWidth'i tab stop pozisyonuna ayarla
    if (group.isTab) {
      lines[lines.length - 1].push(group.segments[0]);
      const prevLineWidth = currentLineWidth;
      const nextTabStop = tabStops.find(t => t > currentLineWidth);
      if (nextTabStop !== undefined) {
        currentLineWidth = nextTabStop;
      } else {
        const defaultTabWidth = (baseHeight / capHeightRatio) * 4;
        currentLineWidth = Math.ceil(currentLineWidth / defaultTabWidth) * defaultTabWidth + defaultTabWidth;
      }
       continue;
    }

    // Grup içindeki segment'leri işle
    const groupSegments = group.segments;

    // Grup içinde \n var mı kontrol et
    let hasNewline = false;
    for (const seg of groupSegments) {
      if (seg.text && seg.text.includes('\n')) {
        hasNewline = true;
        break;
      }
    }

    // Eğer \n varsa, segment'leri tek tek işle (eski mantık)
    if (hasNewline) {
      for (const seg of groupSegments) {
        if (seg.isStackedGroup) {
          lines[lines.length - 1].push(seg);
          currentLineWidth += calculateSegmentWidth(seg, capHeightRatio);
          isFirstLine = false;
          continue;
        }

        if (!seg.text) continue;

        const troikaFontSize = seg.height / capHeightRatio;
        const parts = seg.text.split('\n');

        for (let partIndex = 0; partIndex < parts.length; partIndex++) {
          if (partIndex > 0) {
            lines.push([]);
            isFirstLine = true;
            currentLineWidth = leftIndent + firstLineIndent;
          }

          const part = parts[partIndex];
          if (!part) continue;

          if (maxWidth === Infinity) {
            lines[lines.length - 1].push({ ...seg, text: part });
            currentLineWidth += estimateTextWidth(part, troikaFontSize, seg.fontName, seg.bold, seg.italic);
            isFirstLine = false;
            continue;
          }

          // Kelime kelime işle
          processTextWithWordWrap(seg, part, troikaFontSize, lines, maxWidth, leftIndent, firstLineIndent,
            () => isFirstLine, (val) => { isFirstLine = val; },
            () => currentLineWidth, (val) => { currentLineWidth = val; });
        }
      }
      continue;
    }

    // \n yoksa, grubun toplam genişliğini hesapla
    let groupWidth = 0;
    for (const seg of groupSegments) {
      groupWidth += calculateSegmentWidth(seg, capHeightRatio);
    }

    // İlk satır minimum genişliği
    const lineMinWidth = isFirstLine ? (leftIndent + firstLineIndent) : leftIndent;
    const willFit = maxWidth === Infinity || currentLineWidth + groupWidth <= maxWidth || currentLineWidth <= lineMinWidth;

    // Grup satıra sığar mı?
    if (willFit) {
      // Sığar, tüm grubu ekle
      for (const seg of groupSegments) {
        lines[lines.length - 1].push(seg);
        const segWidth = calculateSegmentWidth(seg, capHeightRatio);
        const segText = seg.isStackedGroup ? `^${seg.superText || ''}_${seg.subText || ''}` : seg.text;
         currentLineWidth += segWidth;
      }
      isFirstLine = false;
    } else {
      // Sığmaz - segment içindeki kelimeleri ayrı ayrı wrap et
      // Index ile iterate et (sonraki segment'e bakabilmek için)
      for (let segIndex = 0; segIndex < groupSegments.length; segIndex++) {
        const seg = groupSegments[segIndex];

        if (seg.isStackedGroup) {
          // Stacked text - önceki kelime ile birlikte değerlendirilmiş olmalı
          // Sadece ekle (wrap kararı önceki kelimede verildi)
          lines[lines.length - 1].push(seg);
          const segWidth = calculateSegmentWidth(seg, capHeightRatio);
           currentLineWidth += segWidth;
          continue;
        }

        if (!seg.text) continue;

        const troikaFontSize = seg.height / capHeightRatio;

        // Segment içindeki kelimeleri ayır
        const words = seg.text.split(/(\s+)/);
        let currentSegmentText = '';
        let pendingSpaceWidth = 0; // Bekleyen boşluk genişliği

        // Segment sonunda boşluk var mı? (bitişik stacked kontrolü için)
        const lastPart = words[words.length - 1];
        const segmentEndsWithSpace = lastPart && /^\s+$/.test(lastPart);

        // Sonraki segment stacked mi?
        const nextSegment = groupSegments[segIndex + 1];
        const nextIsStacked = nextSegment && nextSegment.isStackedGroup;

        for (let wordIndex = 0; wordIndex < words.length; wordIndex++) {
          const word = words[wordIndex];
          if (!word) continue;

          const isWhitespace = /^\s+$/.test(word);
          const wordWidth = estimateTextWidth(word, troikaFontSize, seg.fontName, seg.bold, seg.italic);

          if (isWhitespace) {
            currentSegmentText += word;
            pendingSpaceWidth += wordWidth;
            continue;
          }

          // Bu segment'in son kelimesi mi? (sonraki stacked kontrolü için)
          const isLastWordInSegment = words.slice(wordIndex + 1).every(w => !w || /^\s+$/.test(w));

          // Bitişik stacked var mı? (segment sonunda boşluk yoksa ve sonraki segment stacked ise)
          const hasAttachedStacked = isLastWordInSegment && !segmentEndsWithSpace && nextIsStacked;

          // Toplam genişlik hesapla (bitişik stacked dahil)
          let totalWidthToCheck = pendingSpaceWidth + wordWidth;
          if (hasAttachedStacked) {
            totalWidthToCheck += calculateSegmentWidth(nextSegment, capHeightRatio);
          }

          // Kelime (+ bitişik stacked) mevcut satıra sığar mı?
          const testWidth = currentLineWidth + totalWidthToCheck;
          const lineMin = isFirstLine ? (leftIndent + firstLineIndent) : leftIndent;
          const fits = testWidth <= maxWidth || currentLineWidth <= lineMin;

 

          if (fits) {
            // Sığar
            currentSegmentText += word;
            currentLineWidth = currentLineWidth + pendingSpaceWidth + wordWidth;
            pendingSpaceWidth = 0;
          } else {
            // Sığmaz - mevcut segment'i bitir, yeni satıra geç
            if (currentSegmentText.trim()) {
              lines[lines.length - 1].push({ ...seg, text: currentSegmentText.trimEnd() });
            }
            lines.push([]);
            isFirstLine = false;
            currentLineWidth = leftIndent + wordWidth;
            currentSegmentText = word;
            pendingSpaceWidth = 0;
          }
        }

        // Kalan text'i ekle
        if (currentSegmentText.trim()) {
          lines[lines.length - 1].push({ ...seg, text: currentSegmentText.trimEnd() });
        }
      }
      isFirstLine = false;
    }
  }

  // Boş satırları temizle
  return lines.filter(line => line.length > 0);
}

/**
 * Text'i word-wrap ile işle (yardımcı fonksiyon)
 */
function processTextWithWordWrap(seg, text, fontSize, lines, maxWidth, leftIndent, firstLineIndent, getIsFirstLine, setIsFirstLine, getCurrentLineWidth, setCurrentLineWidth) {
  const words = text.split(/(\s+)/);
  let currentSegmentText = '';
  let currentLineWidth = getCurrentLineWidth();
  let isFirstLine = getIsFirstLine();
  const lineMinWidth = isFirstLine ? (leftIndent + firstLineIndent) : leftIndent;
  let trailingSpaceWidth = 0;

  for (const word of words) {
    if (!word) continue;

    const isWhitespace = /^\s+$/.test(word);
    const wordWidth = estimateTextWidth(word, fontSize, seg.fontName, seg.bold, seg.italic);

    if (isWhitespace) {
      currentSegmentText += word;
      trailingSpaceWidth += wordWidth;
      continue;
    }

    const effectiveLineWidth = currentLineWidth - trailingSpaceWidth;

    if (effectiveLineWidth + wordWidth <= maxWidth || effectiveLineWidth <= lineMinWidth) {
      currentLineWidth = effectiveLineWidth + trailingSpaceWidth + wordWidth;
      currentSegmentText += word;
      trailingSpaceWidth = 0;
    } else {
      if (currentSegmentText) {
        const trimmedText = currentSegmentText.trimEnd();
        if (trimmedText) {
          lines[lines.length - 1].push({ ...seg, text: trimmedText });
        }
      }

      lines.push([]);
      isFirstLine = false;
      currentLineWidth = leftIndent;
      trailingSpaceWidth = 0;

      currentSegmentText = word;
      currentLineWidth = leftIndent + wordWidth;
    }
  }

  if (currentSegmentText) {
    lines[lines.length - 1].push({ ...seg, text: currentSegmentText });
  }

  setIsFirstLine(isFirstLine);
  setCurrentLineWidth(currentLineWidth);
}

/**
 * Tek mesh oluştur (basit durumlar için)
 */
function createSingleMesh(entity, displayText, baseColor, baseHeight, textAlign, anchor, rotationRad, rawText, globalFormatting, segments) {
  const CAP_HEIGHT_RATIO = 0.70; // AutoCAD'e daha yakın görünüm için
  const fontName = entity.fontPath || '/fonts/Arial.ttf';

  const mesh = new Text();
  const troikaFontSize = baseHeight / CAP_HEIGHT_RATIO;

  // Segment'lerden renk al (varsa)
  const effectiveColor = segments.length > 0 ? segments[0].color : baseColor;

  mesh.fontSize = troikaFontSize;
  mesh.font = fontName;
  mesh.color = effectiveColor;

  // ✅ Transparency desteği
  if (entity.opacity !== undefined && entity.opacity < 1) {
    mesh.fillOpacity = 1 - entity.opacity;
  }

  if (entity.width && entity.width > 0) {
    mesh.maxWidth = entity.width;
    mesh.overflowWrap = 'break-word';
    mesh.whiteSpace = 'normal';
  }

  mesh.text = displayText || 'NO TEXT';
  mesh.textAlign = textAlign;
  mesh.anchorX = anchor.x;
  mesh.anchorY = anchor.y;
  mesh.rotation.z = rotationRad;
  mesh.position.set(entity.position?.x || 0, entity.position?.y || 0, 0);

  mesh.userData = {
    id: generateUniqueId('mtext'),
    dxfHandle: entity.handle,
    type: 'mtext',
    position: entity.position,
    rawText: rawText,
    plainText: displayText,
    baseFontSize: baseHeight,
    baseColorIndex: entity.colorIndex,
    baseColor: entity.color,
    layerColor: entity.layerColor,
    renderedColor: effectiveColor,
    width: entity.width,
    attachmentPoint: entity.attachmentPoint,
    rotation: entity.rotation || (rotationRad * 180 / Math.PI),
    directionVector: entity.directionVector,
    textAlign: textAlign,
    anchorY: anchor.y,
    originalColor: effectiveColor,
    layerName: entity.layerName || '0',
    segments: segments,
    isSegmented: false,
  };

  mesh.sync(() => {
    if (mesh.textRenderInfo) {
      mesh.userData.troikaBoundingBox = mesh.textRenderInfo.totalBounds;
    }
  });

 
  return mesh;
}

/**
 * Updates the color of an existing Troika Text mesh or Group.
 * @param {Object} textMesh - Text mesh or Group
 * @param {string} newColor - New color to apply, or 'restore' to restore original colors
 */
export function updateMtextColor(textMesh, newColor) {
  if (!textMesh) {
    console.warn('updateMtextColor: textMesh is undefined');
    return;
  }

  // 'restore' özel değeri - orijinal renkleri geri yükle
  if (newColor === 'restore') {
    restoreMtextOriginalColors(textMesh);
    return;
  }

  const clean = typeof newColor === 'string' ? newColor.trim() : newColor;

  // Group ise segment renklerini sakla ve tümüne yeni renk uygula
  if (textMesh.isGroup) {
    // İlk kez hover edildiyse orijinal renkleri sakla
    if (!textMesh.userData.segmentOriginalColors) {
      textMesh.userData.segmentOriginalColors = [];
      textMesh.children.forEach(child => {
        if (child.color !== undefined) {
          textMesh.userData.segmentOriginalColors.push(child.color);
        }
      });
    }

    textMesh.children.forEach(child => {
      if (child.color !== undefined) {
        child.color = clean;
        if (child.sync) child.sync();
      }
    });
    textMesh.userData.renderedColor = clean;
  } else {
    // Troika Text → sync, plain THREE objects → material.color
    if ('color' in textMesh) {
      textMesh.color = clean;
    }
    if (textMesh.material && textMesh.material.color && typeof textMesh.material.color.set === 'function') {
      textMesh.material.color.set(clean);
    }
    if (textMesh.userData) textMesh.userData.renderedColor = clean;
    if (typeof textMesh.sync === 'function') textMesh.sync();
  }
}

/**
 * Restores original colors for MTEXT (handles both single mesh and Group)
 */
export function restoreMtextOriginalColors(textMesh) {
  if (!textMesh) return;

  if (textMesh.isGroup) {
    // Segment renklerini geri yükle
    if (textMesh.userData.segmentOriginalColors) {
      textMesh.children.forEach((child, index) => {
        if (child.color !== undefined && textMesh.userData.segmentOriginalColors[index]) {
          child.color = textMesh.userData.segmentOriginalColors[index];
          if (child.sync) child.sync();
        }
      });
    }
    textMesh.userData.renderedColor = textMesh.userData.originalColor;
  } else {
    // Tek mesh - orijinal rengi geri yükle
    if (textMesh.userData?.originalColor) {
      const orig = textMesh.userData.originalColor;
      if ('color' in textMesh) {
        textMesh.color = orig;
      }
      if (textMesh.material && textMesh.material.color && typeof textMesh.material.color.set === 'function') {
        textMesh.material.color.set(orig);
      }
      textMesh.userData.renderedColor = orig;
      if (typeof textMesh.sync === 'function') textMesh.sync();
    }
  }
}